/* eslint-disable react-hooks/exhaustive-deps */
import * as React from "react";
import { Navigate } from "react-router-dom";
import { useValidatedUserPayload } from "../app/hooks";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";

/**
 * @param groups: An array containing the groups that can see this content.
 * @param element: The element to be displayed
 */
interface SecureRouteProps extends React.PropsWithChildren {
  /**
   * A string array containing the groups that can see this content.
   */
  groups: string[];
  /**
   * The product name the element belongs to.
   */
  productName?: string;
  /**
   * The element to be displayed
   */
  element: JSX.Element;
  /**
   * List of specific usernames that can't access this resource
   */
  forbiddenUsernames?: string[];
}

/**
 * Component that verifies if the user is logged in and the token is valid, and makes sure the user has priviledges to see the component they are looking for
 * @param props groups: A string array containing the groups that can see this content.
 * @param props element: The element to be displayed
 * @returns Depending on the state of the user, it returns the requested module, a log in page, or an "access denied" page
 */
export default function SecureRoute(props: SecureRouteProps) {
  const secureGroups: string[] = ["Administrator", ...props.groups];
  //const navigate = useNavigate();
  //const location = useLocation();
  //const [getProductsQuery, getProductsQueryResult] = useLazyGetProductsQuery();
  const userSession = useValidatedUserPayload();
  const [loading, setLoading] = React.useState<boolean>(true);
  const [groupsAreValid, setGroupsAreValid] = React.useState<boolean>(false);

  const checkGroups = (cognitoGroups: string[]) => {
    let returnValue = cognitoGroups.find(
      (g) => secureGroups && (secureGroups.find((ag) => ag === g) ?? false),
    )
      ? true
      : false;
    return returnValue;
  };

  const isUserAllowed = (username: string | null) => {
    let returnValue = true;
    if (
      props.forbiddenUsernames &&
      username &&
      props.forbiddenUsernames.includes(username)
    ) {
      returnValue = false;
    }
    return returnValue;
  };

  const [userIsValid, setUserIsValid] = React.useState<boolean>(true);

  /*  While this is a good idea, OIDC clients don't go through our normal login flow that includes
    await Auth.signIn() (included in the NewLogInPage.tsx component), therefore this check will
    always return false, which, in turn, redirects the user to the login page, then the callback page,
    and eventually here again, creating a permanent loop.

    const isUserLoggedIn = async () => {
    try{
      const user = await Auth.currentAuthenticatedUser();
      return true;
    } catch {
      return false;
    }
  };
 */
  React.useEffect(() => {
    const checkUser = async () => {
      /*       if (!await isUserLoggedIn()){
        navigate(
          `/login?redirect_uri=${encodeURIComponent(`${location.pathname}${location.search}`)}`,
        );
      } */
      if (userSession && userSession.cognitoGroups && userSession.payload) {
        setGroupsAreValid(checkGroups(userSession.cognitoGroups));
        setUserIsValid(isUserAllowed(userSession.payload["cognito:username"]));
        setLoading(false);
      }
    };
    checkUser();
  }, [userSession, props]);

  return (
    <div style={{ height: "99%" }}>
      {loading && (
        <div className="loading-div">
          <FontAwesomeIcon
            icon={faSpinner}
            size="2x"
            spin={true}
            style={{ margin: "0 auto" }}
          />
        </div>
      )}
      {!loading && (!groupsAreValid || !userIsValid) && (
        <Navigate to={"/error"} />
      )}
      {!loading && groupsAreValid && userIsValid && (
        <div style={{ height: "99%" }}>{props.element}</div>
      )}
    </div>
  );
}
